import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, Paper, TextField, Divider, Grid,
  MenuItem, Select, InputLabel, FormControl, Avatar, Button, Alert
} from '@mui/material';
import LoadingState from './common/LoadingState';

const allowedRoles = ['user', 'admin', 'reviewer', 'author'];

function normalizeRole(role) {
  if (!role) return '';
  if (typeof role === 'string') return role.trim().toLowerCase();
  if (typeof role === 'object' && role.name != null) return String(role.name).trim().toLowerCase();
  return '';
}

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(user || {});

  // Keep profile in sync with auth user (role from JWT via /auth/me)
  useEffect(() => {
    if (!user) return;
    setProfile({
      ...user,
      role: normalizeRole(user.role),
    });
  }, [user]);

  if (!user) return <LoadingState label="Loading profile…" sx={{ mt: 6 }} />;

  const jwtRole = normalizeRole(user.role);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem', fontWeight: 700 }}>
            {profile.firstName?.charAt(0) || profile.email?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {[profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Profile'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              value={profile.firstName || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              value={profile.lastName || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              value={profile.email || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled>
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                label="Role"
                value={jwtRole || ''}
              >
                {allowedRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Role comes from your sign-in session (JWT). Sign out and sign in again after a role change.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
